import React, { useEffect, useState } from 'react';
import { format, isSameDay } from 'date-fns';
import { hu } from 'date-fns/locale';
import clsx from 'clsx';
import { Event, eventGroupLabel } from '../../types/event';
import { getCalendarDays, HUNGARIAN_WEEKDAYS_SHORT, formatEventTime, getEventColor, isAllDayEvent, CalendarDay } from '../../utils/calendar';
import { useIsMobile } from '../../hooks/useIsMobile';

interface MonthViewProps {
  currentDate: Date;
  events: Event[];
  onEventClick: (event: Event) => void;
  onDateClick: (date: Date) => void;
  canModifyEvents: boolean;
}

const MonthView: React.FC<MonthViewProps> = ({
  currentDate,
  events,
  onEventClick,
  onDateClick,
  canModifyEvents,
}) => {
  const calendarDays = getCalendarDays(currentDate, events);
  const isMobile = useIsMobile();
  // Mobilon a napra koppintva a nap eseményei jelennek meg a rács alatt
  // Alapból a mai nap van kijelölve (ha a látott hónapban van), így a nap listája
  // rögtön látszik, és kiderül, hogy a napokra koppintani lehet
  const todayIfShown = () => {
    const today = new Date();
    return today.getFullYear() === currentDate.getFullYear() && today.getMonth() === currentDate.getMonth()
      ? today
      : null;
  };
  const [selectedMobileDate, setSelectedMobileDate] = useState<Date | null>(todayIfShown);

  useEffect(() => {
    setSelectedMobileDate(todayIfShown());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate]);

  const selectedMobileDay: CalendarDay | null = selectedMobileDate
    ? calendarDays.find((d) => isSameDay(d.date, selectedMobileDate)) ?? null
    : null;

  const handleDayCellClick = (day: CalendarDay) => {
    if (isMobile) {
      setSelectedMobileDate(day.date);
    } else if (canModifyEvents) {
      onDateClick(day.date);
    }
  };

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Weekday Headers */}
      <div className="grid grid-cols-7 border-b border-gray-200">
        {/* Index a kulcs: a rövid nevek ismétlődnek (Sz = szerda és szombat) */}
        {HUNGARIAN_WEEKDAYS_SHORT.map((day, index) => (
          <div
            key={index}
            className="py-2 md:py-3 text-center text-xs md:text-sm font-medium text-gray-700 bg-gray-50"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="flex-1 grid grid-cols-7 auto-rows-fr">
        {calendarDays.map((day, dayIndex) => (
          <div
            key={dayIndex}
            className={clsx(
              'border-r border-b border-gray-200 p-1 md:p-2 min-h-[56px] md:min-h-[120px] cursor-pointer hover:bg-gray-50',
              {
                'bg-gray-50': !day.isCurrentMonth,
                'bg-blue-50': day.isToday,
                'ring-1 ring-inset ring-primary-500':
                  isMobile && selectedMobileDate !== null && isSameDay(day.date, selectedMobileDate),
              }
            )}
            onClick={() => handleDayCellClick(day)}
          >
            {/* Day Number */}
            <div className="flex justify-between items-center mb-1 md:mb-2">
              <span
                className={clsx(
                  'text-sm font-medium',
                  {
                    'text-gray-400': !day.isCurrentMonth,
                    'text-gray-900': day.isCurrentMonth && !day.isToday,
                    'text-blue-600 font-bold': day.isToday,
                  }
                )}
              >
                {format(day.date, 'd')}
              </span>
              {day.isToday && (
                <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
              )}
            </div>

            {/* Events - mobilon csak pöttyök */}
            <div className="md:hidden flex flex-wrap items-center gap-0.5">
              {day.events.slice(0, 4).map((event) => (
                <span
                  key={event.id}
                  className={clsx('inline-block w-2 h-2 rounded-full border', getEventColor(event.type))}
                />
              ))}
              {day.events.length > 4 && (
                <span className="text-[10px] leading-none text-gray-500">
                  +{day.events.length - 4}
                </span>
              )}
            </div>

            {/* Events - teljes megjelenítés (md-től) */}
            <div className="hidden md:block space-y-1">
              {day.events.slice(0, 3).map((event) => (
                <div
                  key={event.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEventClick(event);
                  }}
                  className={clsx(
                    'text-xs p-1 rounded cursor-pointer hover:opacity-80 truncate border transition-opacity',
                    getEventColor(event.type)
                  )}
                  title={`${event.title}${eventGroupLabel(event) ? ` - ${eventGroupLabel(event)}` : ''}${event.location ? ` - ${event.location}` : ''} - ${formatEventTime(event)}`}
                >
                  <div className="font-medium truncate">
                    {event.title}
                    {eventGroupLabel(event) && <span className="opacity-75"> ({eventGroupLabel(event)})</span>}
                  </div>
                  <div className="flex items-center gap-1 text-xs opacity-75">
                    {!isAllDayEvent(event) && (
                      <span>{format(new Date(event.startTime), 'HH:mm')}</span>
                    )}
                    {event.location && (
                      <span className="truncate">· {event.location}</span>
                    )}
                  </div>
                </div>
              ))}
              {day.events.length > 3 && (
                <div className="text-xs text-gray-500 px-1">
                  +{day.events.length - 3} további
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Mobil: kiválasztott nap eseményei a rács alatt */}
      {selectedMobileDay && (
        <div className="md:hidden border-t border-gray-200 bg-white p-4">
          <div className="flex items-center justify-between gap-2 mb-2">
            <h3 className="text-sm font-semibold text-gray-900 capitalize">
              {format(selectedMobileDay.date, 'MMMM d., EEEE', { locale: hu })}
            </h3>
            {canModifyEvents && (
              <button
                onClick={() => onDateClick(selectedMobileDay.date)}
                className="min-h-[36px] px-3 text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                + Új esemény
              </button>
            )}
          </div>
          {selectedMobileDay.events.length === 0 ? (
            <p className="text-sm text-gray-500">Nincs esemény ezen a napon.</p>
          ) : (
            <div className="space-y-2">
              {selectedMobileDay.events.map((event) => (
                <div
                  key={event.id}
                  onClick={() => onEventClick(event)}
                  className={clsx(
                    'p-2 rounded cursor-pointer hover:opacity-80 border transition-opacity',
                    getEventColor(event.type)
                  )}
                >
                  <div className="text-sm font-medium">
                    {event.title}
                    {eventGroupLabel(event) && <span className="opacity-75 font-normal"> ({eventGroupLabel(event)})</span>}
                  </div>
                  <div className="text-xs opacity-75 mt-0.5">
                    {isAllDayEvent(event) ? 'Egész napos' : formatEventTime(event)}
                    {event.location && <span> · {event.location}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MonthView;